import verifyImageAccessibility from './verifyImageAccessibility';
import { _getPrivateUrl } from './__inner/image/_getPrivateUrl';
import { _removeHostOfImageUrl } from './__inner/image/_removeHostOfImageUrl';

export interface PrivateUrlParams {
  urls: string[];
  expireSec: number;
}

/**
 * @description 批量获取屏蔽图片私有bucket的url
 * @param params params: { urls: Urls, expireSec: ExpireSec }
 * @type params.urls: string[], key eg. https://c-ssl.dtstatic.com/uploads/people/201411/01/20141101142720_aYcdX.jpeg
 * @type params.expireSec?: number, expire second, default 3600
 * @param isBlockingList isBlockingList?: boolean[], 『是否是屏蔽图片』list，与urls对应
 * @param callback?: (url: string, index?: number, normal?: boolean) => void, 每张屏蔽图片验证完的回调, 可能是blobURL或httpURL(非屏蔽图片)。在这个callback里你需要将url设置给你要展示的图片list的对应index。normal: 该图片是正常的还是私有屏蔽
 * @return urlList: Promise<string[]>, 如果某个url结果是''则代表不确定图片在哪个bucket，需要自己验证哪个bucket可以访问，可以使用callback或者自己使用verifyImageAccessibility验证
 */
const getPrivateUrlOfImages = async (
  params: PrivateUrlParams,
  isBlockingList: boolean[],
  callback?: (url: string, index?: number, normal?: boolean) => void,
): Promise<string[]> => {
  try {
    const { urls = [], expireSec = 3600 } = params || {};
    const newIndexMapOldIndex: number[] = [];
    const oldIndexMapNewIndex: Record<number, number> = {};
    const blockingUrls = Array.isArray(isBlockingList)
      ? urls.filter((_url, index) => {
          if (isBlockingList[index]) {
            newIndexMapOldIndex.push(index);
            oldIndexMapNewIndex[index] = newIndexMapOldIndex.length - 1;
            return true;
          } else {
            return false;
          }
        })
      : urls;
    if (Array.isArray(isBlockingList) && !isBlockingList.some(isBlocking => isBlocking)) {
      return urls;
    }
    const data = await _getPrivateUrl({
      keys: blockingUrls.map(url => _removeHostOfImageUrl(url)),
      expireSec,
    });
    if (data) {
      const { QINIU, JD } = data;
      if (!Array.isArray(QINIU) || QINIU.length === 0 || !Array.isArray(JD) || JD.length === 0) {
        return urls;
      }

      if (isBlockingList) {
        const mixinURLList: string[] = [...urls];
        newIndexMapOldIndex.forEach(oldIndex => {
          mixinURLList[oldIndex] = '';
        });
        if (typeof callback === 'function') {
          mixinURLList.forEach((url, index) => {
            if (url !== '') {
              // 推迟到下一个micro task，保证外面的then拿到list之后再修改
              setTimeout(() => {
                callback(url, index, true);
              }, 0);
            } else {
              verifyImageAccessibility(
                [
                  JD[oldIndexMapNewIndex[index]].privateUrl,
                  QINIU[oldIndexMapNewIndex[index]].privateUrl,
                ],
                blobURL => callback(blobURL, index),
              );
            }
          });
        }
        return mixinURLList;
      } else {
        const results = QINIU.map(() => '');

        if (typeof callback === 'function') {
          results.forEach((_url, index) => {
            verifyImageAccessibility(
              [
                JD[oldIndexMapNewIndex[index]].privateUrl,
                QINIU[oldIndexMapNewIndex[index]].privateUrl,
              ],
              blobURL => callback(blobURL, index),
            );
          });
        }

        return results;
      }
    } else {
      return urls;
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

export default getPrivateUrlOfImages;
